import { z } from "zod";
export const TASTING_IMAGE_MODELS = {
    "gemini-3.1-flash-image": "Nano Banana 2",
    "gemini-3-pro-image": "Nano Banana Pro",
};
/** The model every second render (the twin a matte is lifted from) and depth read uses. */
export const TASTING_IMAGE_TWIN_MODEL = "gemini-3.1-flash-image";
export const TASTING_IMAGE_SCENE_IDS = [
    "hero",
    "exploded",
    "splash",
    "dust",
    "vitrine",
    "oldmaster",
    "macro",
    "monolith",
];
export const TASTING_IMAGE_SCENES = [
    {
        id: "hero",
        label: "Clean hero",
        hint: "The product alone, lifted onto the stage",
        kind: "matte",
        text: "Show the product on its own as a premium hero shot.",
    },
    {
        id: "exploded",
        label: "Exploded",
        hint: "Layers drift apart, crumbs frozen mid-air",
        kind: "matte",
        text: "Deconstruct it into a dramatic exploded view frozen in mid-air: the pieces or layers drift apart vertically with small gaps, " +
            "crumbs, nuts and flakes of its own ingredients suspended around it, like a product-engineering diagram shot at 1/8000 s.",
    },
    {
        id: "splash",
        label: "Splash",
        hint: "A frozen high-speed burst, best for drinks",
        kind: "matte",
        text: "Freeze a high-speed splash moment: the food bursts upward in a sculptural crown of its own texture, " +
            "droplets and fragments of itself hanging in the air around it, shot at 1/10000 s.",
    },
    {
        id: "dust",
        label: "Protein dust",
        hint: "A glowing plume of powder around it",
        kind: "matte",
        text: "The product levitates slightly above nothing, while a soft plume of fine pale protein powder swirls around and behind it like a nebula, " +
            "lit from behind so the dust glows a faint tea green (#BEF0BF) while the food itself stays its true colour.",
    },
    {
        id: "vitrine",
        label: "Vitrine",
        hint: "An artefact on a stone plinth, spotlit",
        kind: "melt",
        text: "Present it like a priceless artefact in a dark museum: on a rough-hewn black stone plinth, under a single tight overhead spotlight cone " +
            "visible in faint haze, the rest of the gallery fallen into darkness.",
    },
    {
        id: "oldmaster",
        label: "Old master",
        hint: "A Dutch Golden Age still life",
        kind: "melt",
        text: "Re-imagine it as the centrepiece of a Dutch Golden Age still-life oil painting (Willem Kalf, Pieter Claesz): chiaroscuro, " +
            "a single window light from the upper left, a pewter plate, a half-peeled lemon, a brass beaker, a rumpled linen napkin, visible fine oil-paint craquelure.",
    },
    {
        id: "macro",
        label: "Macro world",
        hint: "Its texture as a landscape",
        kind: "melt",
        text: "An extreme macro close-up so its cross-section and texture become a vast landscape: canyon walls, terraces and glossy rivers made of the food itself, " +
            "very shallow depth of field, a single warm light raking across.",
    },
    {
        id: "monolith",
        label: "Monolith",
        hint: "Colossal, in a rewilded landscape at night",
        kind: "melt",
        text: "Make it colossal: the product stands like a monumental megalith, hundreds of metres tall, in a vast rewilded landscape at night under a deep green-black sky " +
            "(wild grasses, a river catching moonlight, distant forest, a few tiny human figures for scale), cinematic, atmospheric haze, graded to near-black greens.",
    },
];
export const tastingImageScene = (id) => TASTING_IMAGE_SCENES.find((s) => s.id === id) ?? TASTING_IMAGE_SCENES[0];
export const TASTING_IMAGE_MOODS = [
    { id: "bright", label: "Bright" },
    { id: "dramatic", label: "Dramatic" },
    { id: "soft", label: "Soft" },
];
export const TASTING_IMAGE_CHIPS = [
    {
        id: "angle",
        label: "Hero angle",
        hint: "Re-shoot it from a three-quarter hero angle",
    },
    {
        id: "cleanup",
        label: "Clean up",
        hint: "Remove hands, clutter and other brands",
    },
    {
        id: "relight",
        label: "Relight",
        hint: "Light it for the stage it lands on",
    },
    { id: "enhance", label: "Enhance", hint: "Appetising texture, crisp detail" },
];
/** How an image is made: the editor's state, kept with every variant it rolls. */
export const tastingImageRecipeSchema = z.object({
    scene: z.enum(TASTING_IMAGE_SCENE_IDS),
    chips: z.object({
        angle: z.boolean(),
        cleanup: z.boolean(),
        relight: z.boolean(),
        enhance: z.boolean(),
    }),
    mood: z.enum(["bright", "dramatic", "soft"]),
    nudge: z.string().max(500),
    /** The full prompt, once someone has taken over the text; the chips stop writing it. */
    custom: z.string().max(8000).nullable(),
    count: z.number().int().min(1).max(4),
    model: z.enum(["gemini-3.1-flash-image", "gemini-3-pro-image"]),
});
export const DEFAULT_TASTING_IMAGE_RECIPE = {
    scene: "hero",
    chips: { angle: true, cleanup: true, relight: true, enhance: true },
    mood: "dramatic",
    nudge: "",
    custom: null,
    count: 3,
    model: "gemini-3.1-flash-image",
};
/** A stored recipe, or the default when there is none or it no longer parses. */
export function readTastingImageRecipe(value) {
    const parsed = tastingImageRecipeSchema.safeParse(value);
    return parsed.success ? parsed.data : DEFAULT_TASTING_IMAGE_RECIPE;
}
/** Whether two recipes would send the same words: what the editor compares to know a preview is stale. */
export function sameTastingImagePrompt(a, b) {
    if (a.custom !== null || b.custom !== null)
        return a.custom === b.custom && a.scene === b.scene;
    return (a.scene === b.scene &&
        a.mood === b.mood &&
        a.nudge.trim() === b.nudge.trim() &&
        TASTING_IMAGE_CHIPS.every((c) => a.chips[c.id] === b.chips[c.id]));
}
/** The colour each theme's page is: what the model draws behind a matte scene. */
export const TASTING_IMAGE_THEME_HEX = {
    dark: "#0D1610",
    light: "#F8FEF6",
};
export const TASTING_IMAGE_THEME_RGB = {
    dark: [13, 22, 16],
    light: [248, 254, 246],
};
/** The opposite extreme a matte's twin is drawn on: the further from the page colour, the cleaner the alpha. */
export const TASTING_IMAGE_TWIN_RGB = {
    dark: [255, 255, 255],
    light: [0, 0, 0],
};
const SPACE = {
    dark: "near-black deep green #0D1610, a dark room",
    light: "pale green-white paper #F8FEF6, a bright airy room",
};
const LIGHT = {
    dark: {
        dramatic: "Light it as a dramatic but appetising dark-studio food shot that really belongs in that space: a soft warm-neutral key light from the upper left, " +
            "bright enough that textures and glossy surfaces read clearly; a warm, low soft strip light from behind-right that puts glints and specular highlights on glossy surfaces " +
            "(chocolate glaze, glass rims and walls) so their shape reads against the dark, but never draws a bright outline or halo around the silhouette; no white bounce light; " +
            "shadow sides pick up only a faint dark-green ambient tint from the surroundings.",
        bright: "Light it brightly for a dark stage: a large soft warm-neutral key light from the upper left and a gentle fill so every texture reads, " +
            "a warm kick light from behind-right for glints on glossy surfaces, never a bright outline or halo around the silhouette, no white bounce light; " +
            "shadow sides pick up only a faint dark-green ambient tint.",
        soft: "Light it softly and moodily for a dark stage: one very large diffused warm key light from the upper left wrapping gently around the product, low contrast, " +
            "no hard highlights, no outline or halo around the silhouette, no white bounce light; shadow sides fade gently into the dark-green surroundings.",
    },
    light: {
        dramatic: "Light it as a crisp editorial food shot in a bright space: a directional warm daylight key from the upper left casting a defined soft shadow, " +
            "glints on glossy surfaces, shadow sides filled by the pale room; no dark outline around the silhouette.",
        bright: "Light it as an airy high-key food shot: soft bright daylight from the upper left, plenty of fill from the pale room, gentle shadows, " +
            "glossy surfaces catching soft highlights; no dark outline around the silhouette.",
        soft: "Light it softly with large diffused window daylight, very gentle shadows and a calm low-contrast look; no dark outline around the silhouette.",
    },
};
/** The words sent with the product's photos, in order. Shown to the editor as the full prompt. */
export function composeTastingImagePrompt(r, theme) {
    const scene = tastingImageScene(r.scene);
    const parts = [
        "Keep this exact food product recognisable: same food, same layers, same vessel.",
        r.chips.cleanup &&
            "Remove hands, clutter, tables and any other brand's packaging.",
        r.chips.angle &&
            "Re-photograph it from a classic three-quarter hero angle, upright and centred.",
        scene.text,
        r.chips.enhance &&
            "Make it look irresistible: crisp detail, appetising texture, fresh glossy surfaces where the food has them.",
        r.nudge.trim(),
        `The product is in a space of ${SPACE[theme]}.`,
        r.chips.relight && LIGHT[theme][r.mood],
        "Keep the light on the food itself neutral so its true colours read; never tint the food green.",
        scene.kind === "matte"
            ? `Everything sits on a perfectly flat, uniform background, exactly ${TASTING_IMAGE_THEME_HEX[theme]} everywhere, with no gradient, no vignette, no texture and no visible floor line; only a soft contact shadow directly beneath the product.`
            : `The scene's edges dissolve smoothly into flat ${TASTING_IMAGE_THEME_HEX[theme]} on all four sides, so it melts seamlessly into a web page of that colour.`,
        "No text, no logos.",
    ];
    return parts.filter(Boolean).join(" ");
}
/** What a roll sends: the custom text when someone took it over, otherwise what the chips compose. */
export const tastingImagePrompt = (r, theme) => r.custom ?? composeTastingImagePrompt(r, theme);
/** The second render a matte needs: the same image on the opposite extreme of the theme. */
export const tastingImageTwinPrompt = (theme) => `Change only the background to perfectly flat uniform pure ${theme === "dark" ? "white (#FFFFFF)" : "black (#000000)"}. ` +
    "Keep every object, particle, crumb, droplet and dust wisp, their lighting, colours, positions and sizes exactly identical. Keep the contact shadow.";
//# sourceMappingURL=tasting-image.js.map